/** 大模型配置：运营场景分别配置服务商、模型和提示词，Key 在集成页维护。 */
import {
  type LandingAiConfig,
  LandingAiConfigSchema,
  type LandingAiScene,
} from '@novel-admin/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useLandingAiConfig } from '~/data/queries';
import { landingAiConfigApi } from '~/hooks/use-landing-ai-config';
import { useAuthStore } from '~/stores/auth-store';
import { FormRow } from '~/ui/FormRow';
import { Select } from '~/ui/Select';
import { toast } from '~/ui/Toast';
import { SectionHead } from './settings-shared';
function ConfigForm({ initial, scene }: { initial: LandingAiConfig; scene: LandingAiScene }) {
  const [form, setForm] = useState(() => LandingAiConfigSchema.parse(initial));
  const [error, setError] = useState('');
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (input: LandingAiConfig) => landingAiConfigApi.save(input, scene),
    onSuccess: async () => {
      await qc.invalidateQueries({
        queryKey: ['model-configurations', `landing-html-${scene}`],
      });
      toast('大模型配置已保存', { tone: 'pos' });
    },
    onError: (e) => setError(e.message),
  });
  const submit = () => {
    const result = LandingAiConfigSchema.safeParse(form);
    if (!result.success) {
      setError(result.error.issues[0]?.message ?? '配置错误');
      return;
    }
    setError('');
    save.mutate(result.data);
  };
  return (
    <div className="card" style={{ padding: 18 }}>
      <h2>广告落地页 HTML AI {scene === 'review' ? '审核' : '脱敏'}</h2>
      <FormRow label="模型服务商" hint="服务凭据在「集成」中配置；审核和脱敏可以分别选择服务商">
        <Select
          value={form.provider}
          disabled={save.isPending}
          onChange={(e) =>
            setForm({ ...form, provider: e.target.value as LandingAiConfig['provider'], model: '' })
          }
        >
          <option value="openrouter">OpenRouter</option>
          <option value="bailian">阿里云百炼</option>
          <option value="ollama">Ollama（本地模型）</option>
          <option value="huggingface">Hugging Face</option>
        </Select>
      </FormRow>
      <FormRow
        label="模型 ID"
        hint={
          form.provider === 'ollama'
            ? '填写本机已安装的完整模型名，如 huihui_ai/qwen3.8-abliterated:27b-q6_K'
            : form.provider === 'bailian'
              ? '填写支持 JSON 输出的百炼模型 ID，如 qwen-plus；切换服务商后重新填写模型'
              : form.provider === 'huggingface'
                ? '填写 Hugging Face 模型 ID，可用「:服务商」指定推理服务商，如 huihui-ai/Huihui-Qwen3.8-27B-abliterated:featherless-ai；专用端点按端点要求填写'
                : 'OpenRouter 模型 ID，如 provider/model；留空表示尚未选择模型'
        }
      >
        <input
          aria-label="模型 ID"
          className="input mono"
          value={form.model}
          onChange={(e) => setForm({ ...form, model: e.target.value })}
        />
      </FormRow>
      <FormRow
        label={scene === 'review' ? '审核提示词' : '脱敏提示词'}
        required
        hint={
          scene === 'review'
            ? '审核 HTML 文本，输出违规词句及类别，保留原文引用'
            : '输入敏感词句 JSON，模型填入 safe_content，保持原文语言'
        }
      >
        <textarea
          aria-label={scene === 'review' ? '审核提示词' : '脱敏提示词'}
          className="input"
          rows={22}
          value={form.prompt}
          onChange={(e) => setForm({ ...form, prompt: e.target.value })}
        />
      </FormRow>
      {error && (
        <div role="alert" style={{ color: 'var(--neg)' }}>
          {error}
        </div>
      )}
      <button className="btn btn-accent" disabled={save.isPending} onClick={submit}>
        {save.isPending ? '保存中…' : '保存配置'}
      </button>
    </div>
  );
}
function SceneConfig({ scene }: { scene: LandingAiScene }) {
  const q = useLandingAiConfig(true, scene);
  return q.isPending ? (
    <p>加载配置…</p>
  ) : q.isError ? (
    <div role="alert">{q.error.message}</div>
  ) : (
    <ConfigForm initial={q.data} scene={scene} />
  );
}
export default function ModelConfigurationsPage() {
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'root' || role === 'admin';
  return (
    <>
      <SectionHead title="大模型配置" sub="按业务场景维护模型与提示词，服务凭据在「集成」中配置" />
      {canManage ? (
        <div className="vstack" style={{ gap: 16 }}>
          <SceneConfig scene="review" />
          <SceneConfig scene="desensitization" />
        </div>
      ) : (
        <p>仅管理员可以管理大模型配置。</p>
      )}
    </>
  );
}
